import { Lock, Swords } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { useActiveFrame, useBattles, useScenarios, useStartBattle } from '../api/hooks.js';
import { Loading, Page, PageHeader, errorMessage } from '../components/Page.js';
import { Alert, Badge, Button, EmptyState, Panel } from '../components/ui.js';
import { useFeature } from '../lib/player.js';
import s from './ArenaPage.module.css';

export function ArenaPage() {
  const feature = useFeature('arena');
  const scenarios = useScenarios();
  const battles = useBattles();
  const frame = useActiveFrame();
  const start = useStartBattle();
  const navigate = useNavigate();
  if (scenarios.isLoading || !frame) return <Loading />;
  if (!feature.unlocked) {
    return (
      <Page>
        <Panel cut>
          <EmptyState icon={<Lock size={28} />} title="Arena locked">
            <p>The Training Arena opens at player level {feature.minLevel}, together with the Workshop.</p>
            <Link to="/academy">
              <Button variant="primary">Continue in the Academy</Button>
            </Link>
          </EmptyState>
        </Panel>
      </Page>
    );
  }
  return (
    <Page>
      <PageHeader
        eyebrow="Deploy"
        title="Training Arena"
        description="Battles are simulated on the server, tick by tick, using exactly the code installed in your Frame. You don't press buttons — your routines fight."
      />
      {start.error && <Alert>{errorMessage(start.error)}</Alert>}
      <div className={s.grid}>
        <div className={s.col}>
          {scenarios.data?.map((sc) => (
            <Panel key={sc.id} label={sc.kind} title={sc.name} cut>
              <div className={s.scenario}>
                <p>{sc.description}</p>
                <div className={s.row}>
                  {sc.enemies.map((e, i) => (
                    <Badge key={i} tone="err">
                      {e.name} · {e.maxHealth} HP
                    </Badge>
                  ))}
                  <span className={s.spacer} />
                  <Badge tone="accent">+{sc.rewards.playerXp} XP</Badge>
                  {sc.rewardsRemainingToday !== null && <Badge>{sc.rewardsRemainingToday} rewarded wins left today</Badge>}
                </div>
                <div className={s.row}>
                  <Button
                    variant="primary"
                    size="large"
                    icon={<Swords size={16} />}
                    disabled={!sc.unlocked}
                    loading={start.isPending && start.variables === sc.id}
                    onClick={() => start.mutate(sc.id, { onSuccess: (b) => navigate(`/arena/battles/${b.id}`) })}
                  >
                    Deploy Frame
                  </Button>
                  {!sc.unlocked && <span className="muted">{sc.lockedReason}</span>}
                </div>
              </div>
            </Panel>
          ))}
        </div>
        <div className={s.col}>
          <Panel label="Deploying" title={frame.name} actions={<Link to="/frame"><Button size="small" variant="ghost">Frame bay</Button></Link>}>
            {frame.software.map((slot) => (
              <div key={slot.slotIndex} className={s.routine}>
                <span>
                  <span className="label">Thread {slot.slotIndex}</span>
                  <div>{slot.module ? slot.module.name : <span className="muted">Empty</span>}</div>
                </span>
                {slot.module && <Badge tone={slot.module.origin === 'factory' ? 'neutral' : 'accent'}>{slot.module.origin === 'factory' ? 'Factory' : `v${slot.module.version}`}</Badge>}
              </div>
            ))}
          </Panel>
          <Panel label="Recent battles" flush>
            {battles.data?.length ? (
              battles.data.map((b) => (
                <Link key={b.id} to={`/arena/battles/${b.id}`} className={s.battle}>
                  <span>{b.scenarioName}</span>
                  <span className="mono muted">{b.ticks} ticks</span>
                  <Badge tone={b.outcome === 'victory' ? 'ok' : 'err'}>{b.outcome}</Badge>
                </Link>
              ))
            ) : (
              <div style={{ padding: 16 }} className="muted">
                No battles yet.
              </div>
            )}
          </Panel>
        </div>
      </div>
    </Page>
  );
}
